import { ScrollReveal } from "@/components/base/ScrollAnimations";

const capacityMetrics = [
  { label: "Project Areas", value: "11", icon: "ri-folder-open-line" },
  { label: "FSD Programs", value: "18+", icon: "ri-user-star-line" },
  { label: "PSD Programs", value: "32+", icon: "ri-user-follow-line" },
  { label: "Training Hours", value: "256", icon: "ri-time-line" },
];

const certificationLevels = [
  {
    name: "FSD",
    fullName: "Factory Skills Development",
    description:
      "Comprehensive factory-based training for production professionals. Each FSD runs 2 half-days covering Lean Manufacturing, Process Control, Preventive Maintenance, and Lead-Time Compression.",
  },
  {
    name: "PSD",
    fullName: "Professional Skills Development",
    description:
      "Intensive product-focused training for supply chain, finance, HR and merchandising professionals. Each PSD runs 2 half-days covering Communication, Decision Routines, Negotiation, and Change Discipline.",
  },
  {
    name: "Innovation Black Belt",
    fullName: "Innovation Black Belt (IBB)",
    description:
      "Master-level certification earned through 128 hours of project work plus 128 hours of FSD/PSD training. IBB graduates provide Innovation Associate support and documentation excellence for factory Innovation Circles.",
  },
];

const projectAreas = [
  "Reducing water consumption in knit dyeing",
  "Improving RFT percentage",
  "Process loss minimization in cotton processing",
  "Reducing steam consumption in exhaust dyeing",
  "Minimizing crease mark in 100% cotton knit",
  "Controlling Dia-GSM variation in knit fabric",
  "Calibrating water scales in bulk dyeing machines",
  "Reducing spandex breakage in denim washing",
  "Minimizing shrinkage problem in denim washing",
  "Enhanced utilization of ring frame by process reengineering",
  "Optimizing spandex breakage in weft knitted fabric",
];

export default function CapacitySection() {
  return (
    <section className="bg-white py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
              <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                / Training
              </span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl font-bold text-tie-text-primary leading-tight">
              Building the Innovation Workforce
            </h2>
            <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
              Three-tier certification system that develops internal innovation
              capability at every level of the factory - from shop-floor
              operators to strategic management.
            </p>
          </div>
        </ScrollReveal>

        {/* Metrics */}
        <ScrollReveal delay={100}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto mb-10 md:mb-14">
            {capacityMetrics.map((metric) => (
              <div
                key={metric.label}
                className="text-center p-4 md:p-5 rounded-xl bg-tie-grey-light border border-tie-grey-dark"
              >
                <div className="w-10 h-10 rounded-lg bg-tie-teal/10 flex items-center justify-center mx-auto mb-2.5 md:mb-3">
                  <i className={`${metric.icon} text-tie-teal`} />
                </div>
                <div className="font-serif text-2xl md:text-3xl font-bold text-tie-navy">
                  {metric.value}
                </div>
                <div className="text-xs text-tie-text-muted mt-1">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* Certification Levels */}
        <div className="grid md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto mb-10 md:mb-14">
          {certificationLevels.map((level, index) => (
            <ScrollReveal key={level.name} delay={index * 150}>
              <div className="h-full p-5 md:p-6 rounded-xl bg-tie-grey-light border border-tie-grey-dark hover:border-tie-teal/30 transition-all duration-300">
                <div className="flex items-center gap-3 mb-3 md:mb-4">
                  <div className="w-10 h-10 rounded-lg bg-tie-teal/10 flex items-center justify-center">
                    <i className="ri-medal-line text-tie-teal" />
                  </div>
                  <span className="text-xs font-bold text-tie-teal uppercase tracking-wider">
                    Level {index + 1}
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-tie-text-primary mb-1">
                  {level.name}
                </h3>
                <p className="text-xs text-tie-teal font-medium mb-3">
                  {level.fullName}
                </p>
                <p className="text-sm text-tie-text-secondary leading-relaxed">
                  {level.description}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Project Areas */}
        <ScrollReveal delay={200}>
          <div className="max-w-4xl mx-auto">
            <p className="text-center text-xs font-semibold text-tie-text-muted uppercase tracking-wider mb-4 md:mb-5">
              Key Innovation Project Areas
            </p>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {projectAreas.map((area) => (
                <span
                  key={area}
                  className="px-3 py-1.5 rounded-full bg-tie-navy/5 border border-tie-grey-dark text-xs md:text-sm text-tie-text-secondary"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}